import type { ImageAssets } from '@seek/branding-assets';
import { VideoProductPosition } from '@seek/ca-graphql-schema/types';
import type { JobDetailsView } from '@seek/job-details-view';
import type { ComponentProps } from 'react';

import { getPreviewCurrencyLabel } from 'src/app/utils/getPreviewCurrencyLabel';
import {
  type DraftJob,
  DraftJobSalaryCurrency,
  DraftJobWritingMode,
  type GetBrandQuery,
  type GetSuggestedCategoriesQuery,
  type HirerAccount,
  type Location,
} from 'src/graphql/generated';

type translatedLabels = {
  privateCompanyLabel: string;
  hiddenSalaryLabel: string;
  postedDate: string;
  workType: string;
};

type FunctionParams = {
  translatedLabels: translatedLabels;
  draft?: DraftJob | null;
  location?: Location | null;
  categories?: GetSuggestedCategoriesQuery['suggestedCategories'] | null;
  brand?: GetBrandQuery['brand'] | null;
  hirerAccount?: HirerAccount | null;
  selectedRRQuestions?: string[];
  guidedJobDescription?: string;
};

export const getJobDetailsForPreview = ({
  translatedLabels,
  draft,
  categories,
  brand,
  hirerAccount,
  location,
  guidedJobDescription,
  selectedRRQuestions,
}: FunctionParams): ComponentProps<typeof JobDetailsView>['job'] | null => {
  const { privateCompanyLabel, postedDate, hiddenSalaryLabel, workType } =
    translatedLabels;
  const suggestedCategory = categories?.find(
    (c) =>
      String(c.category.subCategoryId) === draft?.categories?.[0].subCategoryId,
  );
  return {
    applyUrl: 'about:blank',
    branding: !draft?.isConfidential
      ? {
          ...draft?.branding,
          assets: brand?.assets as ImageAssets,
        }
      : undefined,
    classification: suggestedCategory?.category.categoryName,
    company: {
      name: draft?.isConfidential
        ? privateCompanyLabel
        : hirerAccount?.businessName || '',
    },
    contactMatches: [],
    currencyLabel: getPreviewCurrencyLabel(
      draft?.salary?.currency || DraftJobSalaryCurrency.AUD,
      location?.country || '',
    ),
    description:
      draft?.jobWritingMode === DraftJobWritingMode.STANDARD
        ? draft?.details || ''
        : guidedJobDescription,
    employerQuestions: selectedRRQuestions || [],
    location: location?.displayDescription,
    phoneNumber: draft?.phoneNumber,
    postedDateFormatted: postedDate,
    salary: draft?.salary?.isHidden
      ? hiddenSalaryLabel
      : draft?.salary?.displayValue || '',
    subClassification: suggestedCategory?.category.subCategoryName,
    title: draft?.titles?.[0]?.title || '',
    url: 'about:blank',
    video: draft?.video && {
      link: draft?.video.url,
      position: VideoProductPosition.Bottom,
    },
    workType,
    isVerified: true,
    restrictedApplication: { label: null },
  };
};
